import { useCallback, useMemo } from 'react';

type Options = {
  channel: 'email' | 'facebook' | 'linkedin' | 'twitter';
  url: string;
} & { track?: () => void };

type UseShareHandler = (
  options: Options,
) => (event?: React.MouseEvent<HTMLButtonElement>) => void;

function encodeUrl(baseUrl: string, query: Record<string, number | string>) {
  const encodedQuery = Object.entries(query).reduce(
    (memo, [key, value], index) =>
      `${memo}${index > 0 ? '&' : ''}${encodeURIComponent(
        key,
      )}=${encodeURIComponent(value)}`,
    '',
  );

  return encodeURIComponent(
    `${baseUrl}${baseUrl.indexOf('?') === -1 ? '?' : '&'}${encodedQuery}`,
  );
}

const useShareHandler: UseShareHandler = (options) => {
  const [href, width, height] = useMemo(() => {
    switch (options.channel) {
      case 'facebook': {
        const url = encodeUrl(options.url, { share: 'fb' });
        return [`https://www.facebook.com/sharer.php?u=${url}`, 620, 436];
      }
      case 'linkedin': {
        const url = encodeUrl(options.url, { share: 'li' });
        return [
          `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
          620,
          338,
        ];
      }
      case 'twitter': {
        const url = encodeUrl(options.url, { share: 'twt' });
        const text = encodeURIComponent('Check out this job on SEEK:');
        return [`https://twitter.com/share?text=${text}&url=${url}`, 620, 338];
      }
      default: {
        return [];
      }
    }
  }, [options]);

  const emailShare = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (event) {
        event.preventDefault();
      }
      window.location.href = options.url;
      if (options.track) {
        options.track();
      }
    },
    [options],
  );

  const socialShare = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (event) {
        event.preventDefault();
      }
      window.open(
        href,
        'sharer',
        `toolbar=0,status=0,width=${width},height=${height}`,
      );
      if (options.track) {
        options.track();
      }
    },
    [href, height, width, options],
  );

  return options.channel === 'email' ? emailShare : socialShare;
};
export default useShareHandler;
